/**
 * Warehouse picker options from settings.warehouses.list.
 *
 * One entry per CONNECTION, not per synced destination (AI-1370). A connection may resolve no
 * RudderStack destination at all — a workspace can type one in by hand — and building the list from
 * the destination rows dropped exactly those, leaving QueryStep to render its "No connected
 * warehouses" empty state instead of a picker. The backend never had that limit: every routine
 * execution path resolves through the dual-accepting `getWarehouseConnection`.
 *
 * `value` is the connection's addressable id — the destination id when it resolves one (so saved
 * routines keep working and the graph node's `warehouseId` field is unchanged), otherwise the
 * connection's own uuid. That is the same vocabulary the public MCP hands out.
 */
import { connectionDisplayLabel, disambiguateConnectionNames } from '@server/warehouse-keys';
import { useQuery } from '@tanstack/react-query';

import { useOrpc } from '@/_shared/hooks/useOrpc';

export interface WarehouseOption {
  /** The ADDRESSABLE id (D2: `rudderDestinationId || id`) — what the graph node's `warehouseId` holds. */
  value: string;
  label: string;
}

export interface UseWarehouseOptionsReturn {
  options: WarehouseOption[];
  isEmpty: boolean;
  isLoading: boolean;
}

/** Canonical warehouse type from a `TYPE|host|db` account key; '' when the key is malformed. */
function typeFromAccountKey(accountKey: string): string {
  return accountKey.split('|')[0] ?? '';
}

export function useWarehouseOptions(): UseWarehouseOptionsReturn {
  const orpc = useOrpc();
  const { data, isLoading } = useQuery(orpc.settings.warehouses.list.queryOptions({ input: {} }));

  const warehouses = data?.warehouses ?? [];
  const connections = data?.connections ?? [];

  const destinationById = new Map(warehouses.map((w) => [w.rudderDestinationId, w]));

  // The list arrives fanned out — one row per (connection, destination) pair — so a bucket-wide
  // connection covering three destinations arrives three times under one uuid. Each of those rows
  // is a distinct destination and stays a distinct option; dedupe is on the addressable id alone.
  const seen = new Set<string>();
  const picked: { value: string; label: string }[] = [];
  for (const connection of connections) {
    const value = connection.rudderDestinationId || connection.id;
    if (!value || seen.has(value)) continue;
    seen.add(value);

    const destination = connection.rudderDestinationId
      ? destinationById.get(connection.rudderDestinationId)
      : undefined;
    // The bound account's name (unless Lookout-minted), then the destination's own name when one
    // resolves, then the server's label built from the safe dims (`<database>`) — never the host,
    // never a placeholder. `type` only feeds that function's fallback, so the account key's canonical
    // type prefix covers the destination-less case, which the redacted connection row carries no
    // `type` for.
    const label = connectionDisplayLabel({
      rsAccountName: connection.rsAccountName,
      destinationName: destination?.name,
      accountKey: connection.accountKey,
      type: typeFromAccountKey(connection.accountKey),
    });
    picked.push({ value, label });
  }

  // Two entries can render the same string — the labels above are unique only up to the host dim,
  // which is never shown, and two destinations may simply share a name. A picker with two identical
  // rows is a choice nobody can make, so colliding labels get a short id suffix (AI-1370).
  //
  // The suffix is minted from `value`, the ADDRESSABLE id, not from the connection uuid. `picked` is
  // deduped on `value`, so a bucket-wide connection covering several destinations contributes one
  // row per destination under ONE connection uuid: keyed on that, every proposal in the colliding
  // group is the same string, and `disambiguateConnectionNames` drops a group it cannot separate
  // rather than print one suffix twice. The rows stayed identical — the ambiguity this call exists
  // to remove. `value` is unique across `picked` by construction, and for a destination-less
  // connection it IS the connection uuid, so D2's rows suffix exactly as before.
  const labels = disambiguateConnectionNames(picked.map((o) => ({ name: o.label, id: o.value })));
  const options: WarehouseOption[] = picked.map((o, i) => ({ ...o, label: labels[i] ?? o.label }));

  return {
    options,
    isEmpty: !isLoading && options.length === 0,
    isLoading,
  };
}
